// src/utils/address.js
//
// Shared address-handling utilities for the web client.
//
// Customers in Firestore store addresses as 6 separate fields
// (address1, address2, city, state, zip, country). Job/quote/invoice
// documents have historically stored a single concatenated string built
// from a subset of those fields — and the concat logic was duplicated
// across at least 7 sites in inconsistent ways. Some only joined
// address1+city; some dropped country; some used the wrong field names
// entirely (province/postalCode vs state/zip).
//
// This module exists so there is ONE canonical concat plus consistent
// helpers for the address-validation lifecycle. The server-side
// counterpart (functions/utils/addressing.js) implements the same
// concat logic — both must stay in sync.
//
// PHASE 2 SCOPE adds:
//   - validateAddressViaCF()  — wraps the validateAddress Cloud Function
//   - getDisplayAddress()     — prefers canonical (Google) over built string
//   - hasVerifiedCoords()     — boolean "do we have a confident geocode"
//   - hasGeocodingFailure()   — boolean "address was looked up and not found"

import { httpsCallable } from 'firebase/functions';

// Map ISO alpha-2 country codes to their full names. Google's geocoder
// resolves both, but full names produce more confident results.
const COUNTRY_CODE_MAP = {
    CA:  'Canada',
    US:  'United States',
    USA: 'United States',
    UK:  'United Kingdom',
    GB:  'United Kingdom',
    AU:  'Australia',
    NZ:  'New Zealand',
};

export const expandCountryCode = (code) => {
    if (!code) return '';
    const upper = String(code).toUpperCase();
    return COUNTRY_CODE_MAP[upper] || code;
};

// Canonical concatenation of the 6 customer address fields into a single
// string suitable for geocoding, display, or storage on a snapshot doc
// (job/quote/invoice).
//
// Input shape:
//   { address1?, address2?, city?, state?, zip?, country? }
//
// Output:
//   "123 Main St, Apt 2, Thunder Bay, ON P7B 5A2, Canada"
//   or "" if nothing meaningful is provided.
//
// Behaviour:
//   - Missing/empty pieces are skipped (no leading/trailing/double commas)
//   - state and zip are combined ("ON P7B 5A2") for readability
//   - Country is expanded ("Canada" not "CA") for better geocoding
export const buildAddressString = (parts) => {
    if (!parts || typeof parts !== 'object') return '';
    const trim = (v) => (typeof v === 'string' ? v.trim() : '');
    const address1 = trim(parts.address1);
    const address2 = trim(parts.address2);
    const city     = trim(parts.city);
    const state    = trim(parts.state);
    const zip      = trim(parts.zip);
    const country  = expandCountryCode(trim(parts.country));

    let regionLine = '';
    if (city && (state || zip)) {
        regionLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    } else {
        regionLine = [city, state, zip].filter(Boolean).join(' ');
    }

    return [address1, address2, regionLine, country]
        .filter(Boolean)
        .join(', ');
};

// Returns true when the record has a Google-confirmed geocode we can trust
// for routing, mapping, or display. Records imported from CSV or saved
// before Phase 2 will return false until they're either edited and re-saved
// or backfilled (Phase 5).
export const hasVerifiedCoords = (record) => {
    if (!record) return false;
    return record.addressVerified === true
        && record.addressCoords
        && typeof record.addressCoords.lat === 'number'
        && typeof record.addressCoords.lng === 'number';
};

// Returns true when the record was actively looked up and Google returned
// nothing useful (status: 'not_found' in the CF response). Used to surface
// warnings in route stop cards: "this stop's address may not be findable
// on a map." Distinct from "addressVerified is missing" — that just means
// the record predates Phase 2 and we don't know either way.
export const hasGeocodingFailure = (record) => {
    if (!record) return false;
    return record.addressVerified === false
        && record.addressVerifiedAt   // was actively checked, not legacy
        && !record.addressCoords;
};

// Preferred address string for display. Order of preference:
//   1. addressCanonical — what Google returned (correctly capitalized,
//      with country/postal expanded)
//   2. buildAddressString() — clean concat of the 6 fields
//   3. legacy "address" field if present (job/quote/invoice docs that
//      stored a snapshot before Phase 2)
//
// Always returns a string (never null/undefined). Returns "" if there's
// truly nothing to show.
export const getDisplayAddress = (record) => {
    if (!record) return '';
    if (typeof record.addressCanonical === 'string' && record.addressCanonical.trim()) {
        return record.addressCanonical.trim();
    }
    const built = buildAddressString(record);
    if (built) return built;
    if (typeof record.address === 'string') return record.address.trim();
    return '';
};

// formatDisplayAddress (marker a7c3e91f) -- render a customerDetails.address for
// display, tolerating BOTH shapes Spatchy stores: a plain string (live invoices)
// and an object { line1, line2, city, state, zip, country } (QuickBooks-imported
// invoices). Rendering the object directly as a React child throws "Objects are
// not valid as a React child" and blanks the page. MIRROR of the server helper
// in functions/utils/addressing.js (formatDisplayAddress) and the admin
// InvoiceDetailsView formatAddress -- all three MUST stay in sync. NOTE: reads
// line1/line2 (the imported object's keys), NOT the address1/address2 used by
// buildAddressString/getDisplayAddress above. Always returns a string.
export const formatDisplayAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    if (typeof addr === 'object') {
        return [addr.line1, addr.line2, addr.city, addr.state, addr.zip, addr.country]
            .filter(Boolean).join(', ');
    }
    return '';
};

// ─────────────────────────────────────────────────────────────────────────────
// validateAddressViaCF — wrapper around the validateAddress Cloud Function
//
// Pass either:
//   - parts:         { address1, address2, city, state, zip, country }
//   - addressString: pre-built canonical concat
// (Or both — addressString wins in the CF.)
//
// Returns the CF's response shape:
//   { status: 'ok',         canonical, coords, placeId }
//   { status: 'ambiguous',  alternatives: [{ canonical, coords, placeId, locationType }, ...] }
//   { status: 'not_found' }
//   { status: 'error',      message }
//
// Throws:
//   - 'unauthenticated' if not signed in
//   - 'permission-denied' if user has no org
//   - 'invalid-argument' if neither parts nor addressString is meaningful
//   - 'resource-exhausted' if rate-limited
//
// Callers should wrap in try/catch and treat thrown errors as a soft
// failure ("verification temporarily unavailable, save proceeds normally").
// ─────────────────────────────────────────────────────────────────────────────
export const validateAddressViaCF = async (firebaseFunctions, parts, addressString) => {
    const fn = httpsCallable(firebaseFunctions, 'validateAddress');
    const payload = {};
    if (addressString && typeof addressString === 'string' && addressString.trim()) {
        payload.addressString = addressString.trim();
    } else if (parts) {
        payload.parts = parts;
    }
    const result = await fn(payload);
    return result.data;
};